import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogActionFooter,
  DialogCloseButton,
  DialogCloseIcon,
  DialogContent,
  DialogDescription,
  DialogEmphasis,
  DialogHeader,
  DialogTitle,
} from 'components/ui';
import FirmDocumentDialogDocumentList from './FirmDocumentDialogDocumentList';
import type { FirmDocumentDownloadDialogProps } from './FirmDocumentDownloadDialog.props';
import { FIRM_DOCUMENT_FILE_ACTION_DIALOG_TEST_IDS } from './FirmDocumentFileActionDialogs.testIds';

type DownloadDialogContentState = Pick<FirmDocumentDownloadDialogProps, 'documentCount' | 'fileNames'>;

const getDownloadTitle = (documentCount: number) =>
  documentCount === 1 ? 'Download Document' : `Download ${documentCount} Documents`;

const renderDownloadDescription = (documentCount: number, fileNames?: string[]) => (
  <>
    <span>
      {documentCount === 1
        ? <>Download <DialogEmphasis>{fileNames?.[0] ?? 'this document'}</DialogEmphasis>?</>
        : `Download ${documentCount} selected documents?`}
    </span>
    {documentCount > 1 ? <FirmDocumentDialogDocumentList fileNames={fileNames} /> : null}
  </>
);

const FirmDocumentDownloadDialog = ({
  documentCount,
  fileNames,
  isDownloading = false,
  onClose,
  onConfirm,
  open,
}: FirmDocumentDownloadDialogProps) => {
  const [visibleContent, setVisibleContent] = useState<DownloadDialogContentState>({
    documentCount,
    fileNames,
  });

  // Preserve the previous dialog copy while the close animation runs with empty selection state.
  useEffect(() => {
    if (open && documentCount > 0) {
      setVisibleContent({ documentCount, fileNames });
    }
  }, [documentCount, fileNames, open]);

  const dialogContent = open && documentCount > 0
    ? { documentCount, fileNames }
    : visibleContent;

  return (
    <Dialog
      data-testid={FIRM_DOCUMENT_FILE_ACTION_DIALOG_TEST_IDS.downloadDialog}
      maxWidth="xs"
      onClose={isDownloading ? undefined : onClose}
      open={open}>
      <DialogHeader>
        <DialogTitle>{getDownloadTitle(dialogContent.documentCount)}</DialogTitle>
        <DialogCloseButton
          aria-label="Close download document dialog"
          data-testid={FIRM_DOCUMENT_FILE_ACTION_DIALOG_TEST_IDS.downloadCloseButton}
          disabled={isDownloading}
          onClick={onClose}
          size="small">
          <DialogCloseIcon />
        </DialogCloseButton>
      </DialogHeader>
      <DialogContent>
        <DialogDescription>
          {renderDownloadDescription(dialogContent.documentCount, dialogContent.fileNames)}
        </DialogDescription>
      </DialogContent>
      <DialogActionFooter
        actionButtonTestId={FIRM_DOCUMENT_FILE_ACTION_DIALOG_TEST_IDS.downloadConfirmButton}
        actionLabel={dialogContent.documentCount === 1 ? 'Download Document' : 'Download Documents'}
        cancelButtonTestId={FIRM_DOCUMENT_FILE_ACTION_DIALOG_TEST_IDS.downloadCancelButton}
        isActionLoading={isDownloading}
        isCancelDisabled={isDownloading}
        onAction={onConfirm}
        onCancel={onClose}
      />
    </Dialog>
  );
};

export default FirmDocumentDownloadDialog;
